// src/utils/captureMode.js
//
// Pure capture-mode state machine (WP1, docs/coverage-guided-capture-spec.md
// §5). Tracks the lifecycle of a live capture session on the Launch
// workspace:
//
//   idle --start--> capturing --finalizeStarted--> finalizing
//                       ^                              |
//                       |                     finalizeFailed
//                       |                              |
//                       +------------------------------+
//                       |
//                       |         finalizeSucceeded
//                       |                |
//                       |                v
//                       +---------- drafted
//                     (re-arm on a qualifying rowsArrived while drafted)
//
// The UI component owns the clock (a setInterval tick) and the live-traffic
// wire (TrafficContext's subscribeMessages); this module owns every
// decision — no timers, no fetches, no React, and no Date.now() calls
// anywhere in here. Every time-dependent decision takes `now` (ms epoch) as
// an explicit argument, or reads it off the event payload (`ts`).
//
// Never throws: reduce() degrades unknown/malformed events (and a missing
// or non-object state) to a no-op; the selectors degrade malformed
// state/args to their "nothing to do" answer (false / null).

/** Ad-traffic silence (seconds) required before auto-finalize can fire. */
export const QUIET_SEC = 30;
/** Floor (seconds) between debounced, unrecorded live checklist re-runs. */
export const MIN_RUN_INTERVAL_SEC = 10;
/** Auto-finalize cap per capture session (manual "Finalize now" is not gated by this). */
export const MAX_AUTO_FINALIZES = 3;
/**
 * Row categories (validData.adCategory / classifyAdTraffic) that reset the
 * ad-quiet timer. A row with no category at all is treated the same way —
 * conservative: unknown traffic counts as ad traffic so quiescence never
 * fires on rows the tool couldn't classify.
 */
export const AD_QUIET_CATEGORIES = ['ad_request', 'bid_request', 'creative', 'vast', 'ad_tracking'];

export const PHASE = {
  IDLE: 'idle',
  CAPTURING: 'capturing',
  FINALIZING: 'finalizing',
  DRAFTED: 'drafted',
};

/**
 * initialState() -> the reducer's zero state (phase 'idle', nothing
 * captured yet, auto-finalize on by default).
 */
export function initialState() {
  return {
    phase: PHASE.IDLE,
    startedAt: null,
    lastAdRowAt: null,
    lastLiveRunAt: null,
    hasUnrunRows: false,
    autoFinalizeCount: 0,
    autoFinalizeEnabled: true,
    staleDraft: false,
  };
}

/**
 * True if this batch of row categories should reset the ad-quiet timer: a
 * known ad-traffic category, OR the category list is missing/empty, OR any
 * entry in it is null/undefined. Pure-analytics/non_ad batches (every
 * category known and none of them ad-ish) do not reset the timer.
 */
function resetsQuietTimer(categories, adQuietCategories) {
  if (!Array.isArray(categories) || categories.length === 0) return true;
  return categories.some((cat) => cat == null || adQuietCategories.includes(cat));
}

/**
 * reduce(state, event, opts?) -> next state. Never throws. Unknown event
 * types, or a malformed event/state, return the incoming `state` unchanged.
 * `opts.AD_QUIET_CATEGORIES` may override the quiet-reset category list
 * (test seam, mirrors the other constants' options-bag overrides).
 *
 * Events: {type:'start', ts} · {type:'stop'} ·
 * {type:'rowsArrived', ts, categories:[...]} ·
 * {type:'liveRunCompleted', ts} · {type:'coverageChanged', complete} ·
 * {type:'finalizeStarted'} · {type:'finalizeSucceeded'} ·
 * {type:'finalizeFailed'} · {type:'toggleAutoFinalize'}.
 */
export function reduce(state, event, opts = {}) {
  const s = state && typeof state === 'object' ? state : initialState();
  if (!event || typeof event !== 'object' || typeof event.type !== 'string') return s;

  const adQuietCategories = Array.isArray(opts.AD_QUIET_CATEGORIES)
    ? opts.AD_QUIET_CATEGORIES
    : AD_QUIET_CATEGORIES;

  switch (event.type) {
    case 'start': {
      // Only a fresh idle session, or restarting after a drafted session,
      // may (re)start. Already capturing/finalizing ignores this.
      if (s.phase !== PHASE.IDLE && s.phase !== PHASE.DRAFTED) return s;
      return {
        ...s,
        phase: PHASE.CAPTURING,
        startedAt: event.ts ?? null,
        lastAdRowAt: event.ts ?? null,
        autoFinalizeCount: 0,
        staleDraft: false,
        hasUnrunRows: false,
      };
    }

    case 'stop': {
      // Full reset from any phase; only the auto-finalize toggle survives.
      return { ...initialState(), autoFinalizeEnabled: s.autoFinalizeEnabled };
    }

    case 'rowsArrived': {
      if (s.phase === PHASE.IDLE) return s;
      const resets = resetsQuietTimer(event.categories, adQuietCategories);
      const next = { ...s, hasUnrunRows: true };
      if (resets) {
        next.lastAdRowAt = event.ts ?? s.lastAdRowAt;
        if (s.phase === PHASE.DRAFTED) {
          // Re-arm: the existing draft is now stale rather than deleted.
          next.phase = PHASE.CAPTURING;
          next.staleDraft = true;
        }
      }
      return next;
    }

    case 'liveRunCompleted': {
      return { ...s, lastLiveRunAt: event.ts ?? s.lastLiveRunAt, hasUnrunRows: false };
    }

    case 'coverageChanged': {
      // Coverage is threaded directly into shouldAutoFinalize by the caller
      // on every tick — there is no coverage field in this state shape to
      // update. Recognized as a valid event type for forward-compatibility;
      // currently a no-op.
      return s;
    }

    case 'finalizeStarted': {
      return { ...s, phase: PHASE.FINALIZING };
    }

    case 'finalizeSucceeded': {
      return {
        ...s,
        phase: PHASE.DRAFTED,
        autoFinalizeCount: s.autoFinalizeCount + 1,
        staleDraft: false,
      };
    }

    case 'finalizeFailed': {
      // Retry allowed on next quiescence; the failure reason is the UI's
      // job to surface, never swallowed here.
      return { ...s, phase: PHASE.CAPTURING };
    }

    case 'toggleAutoFinalize': {
      return { ...s, autoFinalizeEnabled: !s.autoFinalizeEnabled };
    }

    default:
      return s;
  }
}

/**
 * shouldLiveRun(state, now, opts?) -> true when a debounced, unrecorded
 * checklist re-run should fire: capturing, rows have arrived since the
 * last live run, and the minimum interval floor has elapsed.
 * `opts.MIN_RUN_INTERVAL_SEC` overrides the default floor.
 */
export function shouldLiveRun(state, now, opts = {}) {
  if (!state || typeof state !== 'object' || typeof now !== 'number') return false;
  if (state.phase !== PHASE.CAPTURING) return false;
  if (!state.hasUnrunRows) return false;

  const minRunIntervalSec =
    typeof opts.MIN_RUN_INTERVAL_SEC === 'number' ? opts.MIN_RUN_INTERVAL_SEC : MIN_RUN_INTERVAL_SEC;
  if (state.lastLiveRunAt == null) return true;
  return now - state.lastLiveRunAt >= minRunIntervalSec * 1000;
}

/**
 * shouldAutoFinalize(state, coverage, now, opts?) -> true when the capture
 * session is quiet, fully covered, under the per-session cap, auto-finalize
 * is enabled, and the last live run has already consumed the newest rows
 * (so `coverage` is guaranteed current — otherwise a fresh, uncovered row
 * that arrived after the last checklist run could slip through).
 * `opts.QUIET_SEC` / `opts.MAX_AUTO_FINALIZES` override the defaults.
 */
export function shouldAutoFinalize(state, coverage, now, opts = {}) {
  if (!state || typeof state !== 'object' || typeof now !== 'number') return false;
  if (!coverage || typeof coverage !== 'object') return false;
  if (state.phase !== PHASE.CAPTURING) return false;
  if (!state.autoFinalizeEnabled) return false;
  if (coverage.complete !== true) return false;
  if (state.lastAdRowAt == null) return false;
  if (state.hasUnrunRows) return false;

  const quietSec = typeof opts.QUIET_SEC === 'number' ? opts.QUIET_SEC : QUIET_SEC;
  if (now - state.lastAdRowAt < quietSec * 1000) return false;

  const maxAutoFinalizes =
    typeof opts.MAX_AUTO_FINALIZES === 'number' ? opts.MAX_AUTO_FINALIZES : MAX_AUTO_FINALIZES;
  if (state.autoFinalizeCount >= maxAutoFinalizes) return false;

  return true;
}

/**
 * quietForMs(state, now) -> ms since the last ad-quiet-resetting row, or
 * null if none has arrived yet this session — feeds the "quiet for Ns"
 * progress-header text. Never throws.
 */
export function quietForMs(state, now) {
  if (!state || typeof state !== 'object' || typeof now !== 'number') return null;
  if (state.lastAdRowAt == null) return null;
  return now - state.lastAdRowAt;
}

export default reduce;
